import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CloudUpload } from "@mui/icons-material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import axios from "axios";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import useApiEp from "../hooks/useApiEp";
import ToolsLayout, { ToolsState } from "../tools/ToolsLayout";
import { invalidateTools } from "../tools/toolsData";
import BannerCropDialog from "./BannerCropDialog";
import {
  BANNER_SIZE_LABEL,
  BANNER_RATIO,
  readBannerDimensions,
  validateBannerSource,
  validateBannerDimensions,
} from "./bannerImage";

export default function BannerForm() {
  const { id } = useParams(),
    navigate = useNavigate(),
    client = useQueryClient();
  const query = useApiEp(`admin/banners/${encodeURIComponent(id || "")}`, Boolean(id));
  const [title, setTitle] = useState(""),
    [file, setFile] = useState(null),
    [preview, setPreview] = useState(""),
    [saving, setSaving] = useState(false),
    [error, setError] = useState("");
  const [cropFile, setCropFile] = useState(null),
    [cropSrc, setCropSrc] = useState(""),
    [preparing, setPreparing] = useState(false);
  const selection = useRef(0);
  useEffect(
    () => () => {
      selection.current += 1;
    },
    [],
  );
  useEffect(() => {
    if (!cropFile) {
      setCropSrc("");
      return;
    }
    const url = URL.createObjectURL(cropFile);
    setCropSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [cropFile]);
  useEffect(() => {
    setTitle(query.data?.title || "");
  }, [query.data]);
  useEffect(() => {
    if (!file) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const chooseImage = async (event) => {
    const next = event.target.files[0];
    event.target.value = "";
    if (!next) return;
    const request = ++selection.current;
    const issue = validateBannerSource(next);
    if (issue) {
      setError(issue);
      return;
    }
    setPreparing(true);
    setError("");
    try {
      const dimensions = await readBannerDimensions(next);
      if (request !== selection.current) return;
      const dimensionIssue = validateBannerDimensions(dimensions);
      if (dimensionIssue) throw new Error(dimensionIssue);
      setCropFile(next);
    } catch (err) {
      if (request === selection.current) setError(err.message);
    } finally {
      if (request === selection.current) setPreparing(false);
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (saving || preparing || cropFile) return;
    if (!title.trim()) {
      setError("Enter a banner title.");
      return;
    }
    if (!id && !file) {
      setError("Upload an image for this banner.");
      return;
    }
    if (id && (!query.data || query.isError)) return;
    setSaving(true);
    setError("");
    try {
      const source = file || query.data?.imageUrl;
      if (!source) throw new Error("Upload a banner image before saving.");
      const issue = validateBannerDimensions(await readBannerDimensions(source), true);
      if (issue) throw new Error(issue);
      const body = new FormData();
      body.append("title", title.trim());
      if (file) body.append("image", file);
      const headers = { ...getHeader().headers };
      Object.keys(headers).forEach((key) => {
        if (key.toLowerCase() === "content-type") delete headers[key];
      });
      await axios({
        method: id ? "put" : "post",
        url: `${API_ENDPOINT}admin/banners${id ? "/" + encodeURIComponent(id) : ""}`,
        data: body,
        headers,
      });
      await invalidateTools(client);
      setSaving(false);
      navigate("/home/banners");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to save the banner. Please try again.",
      );
      setSaving(false);
    }
  };
  const displayImage = preview || query.data?.imageUrl;
  return (
    <ToolsLayout
      title={id ? "Edit banner" : "Add banner"}
      description="Create consistent 2:1 banners for the mobile market slider."
    >
      <section className="tools-panel" style={{ maxWidth: 900, margin: "auto" }}>
        <ToolsState
          loading={Boolean(id) && query.isLoading}
          error={Boolean(id) && query.isError}
          retry={query.refetch}
          empty={Boolean(id) && !query.isLoading && !query.isError && !query.data}
          emptyTitle="Banner not found"
        >
          <Box component="form" onSubmit={submit} sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
              Banner details
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 3 }}>
              Required size: <strong>{BANNER_SIZE_LABEL} (2:1)</strong>. Upload a larger image and
              crop it to fit. The saved file always uses these dimensions.
            </Alert>
            <TextField
              fullWidth
              required
              label="Banner title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={saving || preparing}
              helperText="Use a short, descriptive title to identify this promotion."
            />
            <Box sx={{ my: 3 }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                disabled={saving || preparing}
              >
                {preparing
                  ? "Checking image…"
                  : file
                    ? "Change image"
                    : id
                      ? "Replace image"
                      : "Upload image"}
                <input
                  hidden
                  aria-label="Upload banner image"
                  disabled={saving || preparing}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={chooseImage}
                />
              </Button>
              {file && (
                <Button disabled={saving || preparing} onClick={() => setFile(null)}>
                  Discard new image
                </Button>
              )}
              <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                JPG, PNG or WebP · up to 5 MB · minimum {BANNER_SIZE_LABEL} · cropped to exactly
                2:1.
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Mobile slider preview
            </Typography>
            {displayImage ? (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.default",
                  borderRadius: 2,
                  aspectRatio: String(BANNER_RATIO),
                  overflow: "hidden",
                }}
              >
                <img
                  src={displayImage}
                  alt={title || "Banner preview"}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  aspectRatio: String(BANNER_RATIO),
                  display: "grid",
                  placeItems: "center",
                  p: 2,
                  textAlign: "center",
                  bgcolor: "background.default",
                  borderRadius: 2,
                }}
              >
                <Typography color="text.secondary">
                  Upload and crop an image to preview the mobile slider.
                </Typography>
              </Box>
            )}
            {id && !file && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Existing banners must also match {BANNER_SIZE_LABEL} before saving. Replace the
                image if it uses another size.
              </Typography>
            )}
            {file && (
              <Typography variant="caption" display="block" color="success.main" sx={{ mt: 1 }}>
                Ready to upload · {BANNER_SIZE_LABEL} · JPEG
              </Typography>
            )}
            <div className="tools-actions" style={{ marginTop: 24, justifyContent: "flex-end" }}>
              <Button disabled={saving || preparing} component={Link} to="/home/banners">
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={saving}
                disabled={preparing || Boolean(cropFile) || (!id && !file)}
              >
                Save banner
              </LoadingButton>
            </div>
          </Box>
        </ToolsState>
      </section>
      {cropSrc && (
        <BannerCropDialog
          key={cropSrc}
          src={cropSrc}
          onCancel={() => setCropFile(null)}
          onSave={(cropped) => {
            setFile(cropped);
            setCropFile(null);
            setError("");
          }}
        />
      )}
    </ToolsLayout>
  );
}
